/**
 * WebGL Sprite Renderer with Instanced Rendering
 *
 * Renders hundreds/thousands of sprites in a single draw call using WebGL 2.0 instancing.
 * Each sprite can have its own position, rotation, scale, and sprite sheet index.
 */

// Vertex shader - transforms quad vertices and applies per-instance transforms
const VERTEX_SHADER = `#version 300 es
precision highp float;

// Per-vertex attributes (the quad)
in vec2 a_position;
in vec2 a_texCoord;

// Per-instance attributes
in vec2 a_instancePosition;
in float a_instanceRotation;
in float a_instanceScale;
in float a_instanceSpriteIndex;
in float a_instanceAlpha;

// Uniforms
uniform vec2 u_resolution;
uniform vec2 u_spriteSize;
uniform vec2 u_spriteSheetSize;
uniform float u_spritesPerRow;

// Outputs to fragment shader
out vec2 v_texCoord;
out float v_alpha;

void main() {
  // Apply rotation
  float c = cos(a_instanceRotation);
  float s = sin(a_instanceRotation);
  mat2 rotation = mat2(c, s, -s, c);

  // Scale and rotate the quad vertex
  vec2 scaledPos = a_position * u_spriteSize * a_instanceScale;
  vec2 rotatedPos = rotation * scaledPos;

  // Translate to instance position
  vec2 worldPos = rotatedPos + a_instancePosition;

  // Convert to clip space (-1 to 1)
  vec2 clipPos = (worldPos / u_resolution) * 2.0 - 1.0;
  clipPos.y = -clipPos.y; // Flip Y for canvas coordinates

  gl_Position = vec4(clipPos, 0.0, 1.0);

  // Calculate texture coordinates for this sprite in the sheet
  float spriteIndex = a_instanceSpriteIndex;
  float spriteX = mod(spriteIndex, u_spritesPerRow);
  float spriteY = floor(spriteIndex / u_spritesPerRow);

  vec2 spriteOffset = vec2(spriteX, spriteY) * u_spriteSize;
  vec2 texCoordInSprite = a_texCoord * u_spriteSize;
  v_texCoord = (spriteOffset + texCoordInSprite) / u_spriteSheetSize;

  v_alpha = a_instanceAlpha;
}
`;

// Fragment shader - samples texture and applies alpha
const FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_texCoord;
in float v_alpha;

uniform sampler2D u_texture;

out vec4 fragColor;

void main() {
  vec4 texColor = texture(u_texture, v_texCoord);
  fragColor = vec4(texColor.rgb, texColor.a * v_alpha);
}
`;

export interface SpriteInstance {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  spriteIndex: number;
  alpha: number;
}

export interface SpriteSheetConfig {
  texture: WebGLTexture;
  spriteWidth: number;
  spriteHeight: number;
  sheetWidth: number;
  sheetHeight: number;
  spritesPerRow: number;
}

export class WebGLSpriteRenderer {
  private gl: WebGL2RenderingContext | null = null;
  private canvas: HTMLCanvasElement;
  private program: WebGLProgram | null = null;

  // Buffers
  private quadVAO: WebGLVertexArrayObject | null = null;
  private instanceBuffer: WebGLBuffer | null = null;

  // Instance data
  private instanceData: Float32Array;
  private maxInstances: number;
  private instanceCount = 0;

  // Floats per instance: x, y, rotation, scale, spriteIndex, alpha
  private readonly FLOATS_PER_INSTANCE = 6;

  // Uniform locations
  private uniforms: {
    resolution: WebGLUniformLocation | null;
    spriteSize: WebGLUniformLocation | null;
    spriteSheetSize: WebGLUniformLocation | null;
    spritesPerRow: WebGLUniformLocation | null;
    texture: WebGLUniformLocation | null;
  } = {
    resolution: null,
    spriteSize: null,
    spriteSheetSize: null,
    spritesPerRow: null,
    texture: null,
  };

  // Current sprite sheet config
  private spriteSheet: SpriteSheetConfig | null = null;

  // Context loss handling
  private contextLost = false;
  private onContextLost: (() => void) | null = null;
  private onContextRestored: (() => void) | null = null;

  // CSS dimensions (used for coordinate system, separate from canvas pixel dimensions)
  private cssWidth = 0;
  private cssHeight = 0;

  constructor(maxInstances = 2000) {
    this.maxInstances = maxInstances;
    this.instanceData = new Float32Array(maxInstances * this.FLOATS_PER_INSTANCE);

    // Create canvas
    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.pointerEvents = 'none';
  }

  /**
   * Initialize WebGL context and resources.
   * Call this once before rendering.
   */
  initialize(): boolean {
    const gl = this.canvas.getContext('webgl2', {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false,
      preserveDrawingBuffer: false,
    });

    if (!gl) {
      console.error('[WebGLSpriteRenderer] WebGL 2.0 not supported');
      return false;
    }

    this.gl = gl;

    // Set up context loss handlers
    this.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.contextLost = true;
      console.warn('[WebGLSpriteRenderer] WebGL context lost');
      this.onContextLost?.();
    });

    this.canvas.addEventListener('webglcontextrestored', () => {
      console.log('[WebGLSpriteRenderer] WebGL context restored');
      this.contextLost = false;
      this.initializeGL();
      this.onContextRestored?.();
    });

    return this.initializeGL();
  }

  private initializeGL(): boolean {
    const gl = this.gl;
    if (!gl) return false;

    // Compile shaders
    const vertexShader = this.compileShader(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragmentShader = this.compileShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);

    if (!vertexShader || !fragmentShader) return false;

    // Link program
    this.program = gl.createProgram();
    if (!this.program) return false;

    gl.attachShader(this.program, vertexShader);
    gl.attachShader(this.program, fragmentShader);
    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      console.error('[WebGLSpriteRenderer] Program link error:', gl.getProgramInfoLog(this.program));
      return false;
    }

    // Clean up shaders (they're now part of the program)
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    // Get uniform locations
    this.uniforms.resolution = gl.getUniformLocation(this.program, 'u_resolution');
    this.uniforms.spriteSize = gl.getUniformLocation(this.program, 'u_spriteSize');
    this.uniforms.spriteSheetSize = gl.getUniformLocation(this.program, 'u_spriteSheetSize');
    this.uniforms.spritesPerRow = gl.getUniformLocation(this.program, 'u_spritesPerRow');
    this.uniforms.texture = gl.getUniformLocation(this.program, 'u_texture');

    // Create quad geometry (two triangles)
    const quadVertices = new Float32Array([
      // Position (x, y), TexCoord (u, v)
      -0.5, -0.5,  0, 0,
       0.5, -0.5,  1, 0,
       0.5,  0.5,  1, 1,
      -0.5, -0.5,  0, 0,
       0.5,  0.5,  1, 1,
      -0.5,  0.5,  0, 1,
    ]);

    // Create VAO
    this.quadVAO = gl.createVertexArray();
    gl.bindVertexArray(this.quadVAO);

    // Quad vertex buffer
    const quadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, quadVertices, gl.STATIC_DRAW);

    // Position attribute
    const posLoc = gl.getAttribLocation(this.program, 'a_position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 16, 0);

    // TexCoord attribute
    const texLoc = gl.getAttribLocation(this.program, 'a_texCoord');
    gl.enableVertexAttribArray(texLoc);
    gl.vertexAttribPointer(texLoc, 2, gl.FLOAT, false, 16, 8);

    // Instance buffer
    this.instanceBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.instanceData.byteLength, gl.DYNAMIC_DRAW);

    // Instance attributes
    const stride = this.FLOATS_PER_INSTANCE * 4; // bytes

    const instancePosLoc = gl.getAttribLocation(this.program, 'a_instancePosition');
    gl.enableVertexAttribArray(instancePosLoc);
    gl.vertexAttribPointer(instancePosLoc, 2, gl.FLOAT, false, stride, 0);
    gl.vertexAttribDivisor(instancePosLoc, 1);

    const instanceRotLoc = gl.getAttribLocation(this.program, 'a_instanceRotation');
    gl.enableVertexAttribArray(instanceRotLoc);
    gl.vertexAttribPointer(instanceRotLoc, 1, gl.FLOAT, false, stride, 8);
    gl.vertexAttribDivisor(instanceRotLoc, 1);

    const instanceScaleLoc = gl.getAttribLocation(this.program, 'a_instanceScale');
    gl.enableVertexAttribArray(instanceScaleLoc);
    gl.vertexAttribPointer(instanceScaleLoc, 1, gl.FLOAT, false, stride, 12);
    gl.vertexAttribDivisor(instanceScaleLoc, 1);

    const instanceSpriteLoc = gl.getAttribLocation(this.program, 'a_instanceSpriteIndex');
    gl.enableVertexAttribArray(instanceSpriteLoc);
    gl.vertexAttribPointer(instanceSpriteLoc, 1, gl.FLOAT, false, stride, 16);
    gl.vertexAttribDivisor(instanceSpriteLoc, 1);

    const instanceAlphaLoc = gl.getAttribLocation(this.program, 'a_instanceAlpha');
    gl.enableVertexAttribArray(instanceAlphaLoc);
    gl.vertexAttribPointer(instanceAlphaLoc, 1, gl.FLOAT, false, stride, 20);
    gl.vertexAttribDivisor(instanceAlphaLoc, 1);

    gl.bindVertexArray(null);

    // Enable blending
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    console.log('[WebGLSpriteRenderer] Initialized successfully');
    return true;
  }

  private compileShader(type: number, source: string): WebGLShader | null {
    const gl = this.gl!;
    const shader = gl.createShader(type);
    if (!shader) return null;

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('[WebGLSpriteRenderer] Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  /**
   * Get the WebGL canvas element.
   * Attach this to the DOM, positioned over the main canvas.
   */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  /**
   * Resize the renderer to match dimensions.
   */
  resize(width: number, height: number, pixelRatio = 1): void {
    // Store CSS dimensions for coordinate calculations
    this.cssWidth = width;
    this.cssHeight = height;

    // Set canvas pixel dimensions (higher for crisp rendering on HiDPI)
    this.canvas.width = width * pixelRatio;
    this.canvas.height = height * pixelRatio;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    if (this.gl) {
      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  /**
   * Create a sprite sheet texture from an array of canvases.
   * Returns the config needed for rendering.
   */
  createSpriteSheet(
    sprites: HTMLCanvasElement[],
    spriteWidth: number,
    spriteHeight: number
  ): SpriteSheetConfig | null {
    const gl = this.gl;
    if (!gl || sprites.length === 0) return null;

    // Calculate sheet dimensions (power of 2 for compatibility)
    const spritesPerRow = Math.ceil(Math.sqrt(sprites.length));
    const sheetWidth = this.nextPowerOf2(spritesPerRow * spriteWidth);
    const sheetHeight = this.nextPowerOf2(Math.ceil(sprites.length / spritesPerRow) * spriteHeight);

    // Create a canvas to compose the sprite sheet
    const sheetCanvas = document.createElement('canvas');
    sheetCanvas.width = sheetWidth;
    sheetCanvas.height = sheetHeight;
    const ctx = sheetCanvas.getContext('2d')!;

    // Draw all sprites into the sheet
    for (let i = 0; i < sprites.length; i++) {
      const x = (i % spritesPerRow) * spriteWidth;
      const y = Math.floor(i / spritesPerRow) * spriteHeight;
      ctx.drawImage(sprites[i], x, y);
    }

    // Create WebGL texture
    const texture = gl.createTexture();
    if (!texture) return null;

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sheetCanvas);

    // Use linear filtering for smooth scaling
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const config: SpriteSheetConfig = {
      texture,
      spriteWidth,
      spriteHeight,
      sheetWidth,
      sheetHeight,
      spritesPerRow,
    };

    console.log(`[WebGLSpriteRenderer] Created sprite sheet: ${sprites.length} sprites, ${sheetWidth}x${sheetHeight}`);

    return config;
  }

  private nextPowerOf2(n: number): number {
    return Math.pow(2, Math.ceil(Math.log2(n)));
  }

  /**
   * Set the active sprite sheet for rendering.
   */
  setSpriteSheet(config: SpriteSheetConfig): void {
    this.spriteSheet = config;
  }

  /**
   * Begin a new frame. Clears the instance buffer.
   */
  beginFrame(): void {
    this.instanceCount = 0;
  }

  /**
   * Add a sprite instance to render this frame.
   */
  addSprite(instance: SpriteInstance): void {
    if (this.instanceCount >= this.maxInstances) {
      console.warn('[WebGLSpriteRenderer] Max instances exceeded');
      return;
    }

    const offset = this.instanceCount * this.FLOATS_PER_INSTANCE;
    this.instanceData[offset + 0] = instance.x;
    this.instanceData[offset + 1] = instance.y;
    this.instanceData[offset + 2] = instance.rotation;
    this.instanceData[offset + 3] = instance.scale;
    this.instanceData[offset + 4] = instance.spriteIndex;
    this.instanceData[offset + 5] = instance.alpha;

    this.instanceCount++;
  }

  /**
   * Get number of sprites queued this frame.
   */
  getInstanceCount(): number {
    return this.instanceCount;
  }

  /**
   * Render all sprites added this frame.
   */
  endFrame(): void {
    const gl = this.gl;
    if (!gl || this.contextLost || !this.program || !this.spriteSheet || this.instanceCount === 0) {
      return;
    }

    // Clear with transparent
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Debug: log instance count periodically
    if (Math.random() < 0.01) {
      console.log(`[WebGL] Rendering ${this.instanceCount} sprites`);
    }

    gl.useProgram(this.program);

    // Set uniforms - use CSS dimensions for coordinate system (not canvas pixel dimensions)
    // This ensures screen coordinates work correctly regardless of devicePixelRatio
    gl.uniform2f(this.uniforms.resolution, this.cssWidth, this.cssHeight);
    gl.uniform2f(this.uniforms.spriteSize, this.spriteSheet.spriteWidth, this.spriteSheet.spriteHeight);
    gl.uniform2f(this.uniforms.spriteSheetSize, this.spriteSheet.sheetWidth, this.spriteSheet.sheetHeight);
    gl.uniform1f(this.uniforms.spritesPerRow, this.spriteSheet.spritesPerRow);

    // Bind texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.spriteSheet.texture);
    gl.uniform1i(this.uniforms.texture, 0);

    // Update instance buffer
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.instanceData.subarray(0, this.instanceCount * this.FLOATS_PER_INSTANCE));

    // Draw instanced
    gl.bindVertexArray(this.quadVAO);
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, this.instanceCount);
    gl.bindVertexArray(null);
  }

  /**
   * Check if renderer is ready.
   */
  isReady(): boolean {
    return this.gl !== null && !this.contextLost && this.program !== null;
  }

  /**
   * Set context loss callbacks.
   */
  setContextCallbacks(onLost: () => void, onRestored: () => void): void {
    this.onContextLost = onLost;
    this.onContextRestored = onRestored;
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    const gl = this.gl;
    if (!gl) return;

    if (this.program) gl.deleteProgram(this.program);
    if (this.quadVAO) gl.deleteVertexArray(this.quadVAO);
    if (this.instanceBuffer) gl.deleteBuffer(this.instanceBuffer);
    if (this.spriteSheet?.texture) gl.deleteTexture(this.spriteSheet.texture);

    this.gl = null;
    this.program = null;
  }
}
